type CalibrationEventName =
  | 'drag.start'
  | 'drag.move'
  | 'drag.end'
  | 'text.start'
  | 'text.input'
  | 'text.type'
  | 'text.end'
  | 'cursor.move'
  | 'cursor.sample'
  | 'cursor.publish'
  | 'viewport.pan'
  | 'viewport.zoom'
  | 'node.create'
  | 'write.nodePositions'
  | 'write.nodeName'
  | 'write.nodes'
  | 'write.cursor'
  | 'write.text';

type CalibrationEvent = {
  atMs: number;
  name: CalibrationEventName;
  values: Record<string, number>;
};

export type CalibrationReport = {
  version: 1;
  startedAt: string;
  durationMs: number;
  label: string;
  events: CalibrationEvent[];
  droppedEvents: number;
  counts: Partial<Record<CalibrationEventName, number>>;
};

const calibrationEnabled =
  process.env.NODE_ENV === 'development' &&
  process.env.WITH_CLOUD_BACKEND === 'false' &&
  new URLSearchParams(window.location.search).get('debug') === 'calibration';

export const isOperationCalibrationEnabled = (): boolean => calibrationEnabled;

export class OperationCalibration {
  private startedAtMs = 0;
  private report: CalibrationReport | null = null;

  start(label: string, nowMs: number, startedAt: string): void {
    this.startedAtMs = nowMs;
    this.report = { version: 1, startedAt, durationMs: 0, label, events: [], droppedEvents: 0, counts: {} };
  }

  record(name: CalibrationEventName, values: Record<string, number>, nowMs: number): void {
    if (!this.report) return;
    this.report.counts[name] = (this.report.counts[name] ?? 0) + 1;
    if (this.report.events.length >= 50_000) {
      this.report.droppedEvents++;
      return;
    }
    this.report.events.push({ atMs: Math.max(0, nowMs - this.startedAtMs), name, values });
  }

  stop(nowMs: number): CalibrationReport | null {
    const report = this.report;
    if (!report) return null;
    report.durationMs = Math.max(0, nowMs - this.startedAtMs);
    this.report = null;
    return report;
  }
}

export const operationCalibration = new OperationCalibration();

export const recordCalibrationEvent = (name: CalibrationEventName, values: Record<string, number> = {}): void => {
  if (!isOperationCalibrationEnabled()) return;
  operationCalibration.record(name, values, performance.now());
};

export const textChangeCounts = (before: string, after: string): { inserted: number; deleted: number } => {
  const previous = Array.from(before);
  const next = Array.from(after);
  let start = 0;
  while (start < previous.length && start < next.length && previous[start] === next[start]) start++;
  let previousEnd = previous.length;
  let nextEnd = next.length;
  while (previousEnd > start && nextEnd > start && previous[previousEnd - 1] === next[nextEnd - 1]) {
    previousEnd--;
    nextEnd--;
  }
  return { inserted: nextEnd - start, deleted: previousEnd - start };
};
